import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Alert, AppConfig, SnackbarType } from '@shared/definitions';
import { SnackbarData, SnackbarService } from '@digilize/shared/feature/modules/snackbar';
import { DialogService } from '@digilize/shared/utils/services/src';
import { APP_CONFIG } from '@digilize/shared/utils/tokens/src';
import { Subject, takeUntil } from 'rxjs';
import { GlobalAlertState } from './globalAlertState.service';
import { AlertType } from '../alerts.interface';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import {
  ReportErrorDialogComponent,
  ReportErrorDialogData,
} from '../report-error-dialog/report-error-dialog.component';

const DEFAULT_ALERT_TIME = 10000;

@Component({
  selector: 'lib-alert-bar',
  templateUrl: './alert-bar.component.html',
  styleUrls: ['./alert-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertBarComponent implements OnInit, OnDestroy {
  @ViewChild('message') messageRef?: ElementRef<HTMLElement>;

  destroy$: Subject<boolean> = new Subject<boolean>();
  alert: Alert;
  show = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private globalAlertState: GlobalAlertState,
    private snackbarService: SnackbarService,
    private injector: Injector,
    @Optional() @Inject(APP_CONFIG) private appConfig: AppConfig | null
  ) {}

  ngOnInit(): void {
    this.globalAlertState.alertChange$.pipe(takeUntil(this.destroy$)).subscribe((alert: Alert) => {
      if (alert.type === AlertType.Success) {
        const config: MatSnackBarConfig<SnackbarData> = {
          duration: alert.time || 3000,
          data: { msg: alert.message.message, translate: alert.message.translate },
        };

        this.snackbarService.openSnackbar(SnackbarType.Success, config, 'snackbar-bottom');
      } else {
        this.alert = alert;
        this.show = true;
        this.scheduleClose();
      }
    });
  }

  ngOnDestroy(): void {
    this.cancelClose();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get canReport(): boolean {
    return !!this.appConfig?.errorReports && this.alert?.type === AlertType.Error && !!this.alert?.requestId;
  }

  closeAlert(): void {
    this.cancelClose();
    this.show = false;
  }

  /** Held open while the pointer is on the bar, so the Report link cannot close under it. */
  holdOpen(): void {
    this.cancelClose();
  }

  releaseHold(): void {
    if (this.show) {
      this.scheduleClose();
    }
  }

  report(): void {
    const data: ReportErrorDialogData = {
      requestId: this.alert.requestId,
      errorType: this.alert.apiError?.type ?? null,
      errorKind: this.alert.apiError?.info?.kind ?? null,
      errorMessage: this.messageRef?.nativeElement.textContent.trim() || null,
    };

    this.closeAlert();
    // Resolved on use: the bar also renders in apps without a dialog setup, where injecting it up front
    // would break every alert.
    this.injector
      .get(DialogService)
      .openDialog<ReportErrorDialogData>(ReportErrorDialogComponent, { width: '560px', data });
  }

  // A newer alert replaces the bar's content, so it also replaces the countdown of the one before it.
  private scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.closeAlert(), this.alert?.time || DEFAULT_ALERT_TIME);
  }

  private cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}
